import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, PauseCircle, Users, Share2, MapPin, Route, FileText, Activity, Trash2 } from 'lucide-react';
import { Button } from '@common/Button';
import { formatDate } from '@utils/dateHelpers';
import { auth } from '@config/firebase';
import { ShareSurveyModal } from '../survey/share/ShareSurveyModal';
import { SharedMembersModal } from '../survey/share/SharedMembersModal';
import { DeleteSurveyModal } from '../survey/DeleteSurveyModal';
import { SurveyForm } from '../survey/SurveyForm';
import { logError, logInfo } from '@utils/logger';

const SurveyList = ({
  surveys,
  loading,
  error,
  activeSurvey,
  isRecording,
  onEdit,
  onLoad
}) => {
  const navigate = useNavigate();
  const [showShare, setShowShare] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showEditSurvey, setShowEditSurvey] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [localActiveSurveyId, setLocalActiveSurveyId] = useState(() => localStorage.getItem('activeSurveyId'));
  const [localIsRecording, setLocalIsRecording] = useState(() => localStorage.getItem('isRecording') === 'true');

  const handleSurveyLoad = async (surveyId) => {
    try {
      if (!auth.currentUser) {
        logError('No authenticated user');
        return;
      }

      // Validate surveyId and find the survey in the list
      if (!surveyId || typeof surveyId !== 'string') {
        logError('Invalid survey ID:', surveyId);
        return;
      }

      const survey = surveys.find(s => s.id === surveyId);
      if (!survey) {
        logError('Survey not found:', surveyId);
        return;
      }

      // Check if user has permission to load survey - include shared access
      const isAdmin = auth.currentUser?.email === 'jfprince@soltec.ca' || 
                     auth.currentUser?.email === 'jfprince29@gmail.com' ||
                     auth.currentUser?.email === 'admin@oversizehub.net';
      
      const canLoad = isAdmin ||
                     survey.ownerId === auth.currentUser?.uid || 
                     survey.sharedWith?.includes(auth.currentUser?.uid) ||
                     survey.isShared;

      if (!canLoad) {
        logError('No permission to load survey:', surveyId);
        return;
      }

      await onLoad(surveyId);
      logInfo(`Survey ${surveyId} ${survey.id === localActiveSurveyId ? 'unloaded' : 'loaded'} successfully`);
    } catch (error) {
      logError('Failed to load/unload survey:', error);
    }
  };

  // Status configuration with colors and labels
  const STATUS_CONFIG = {
    active: { color: 'bg-green-500/20 text-green-400', label: 'Active' },
    pending: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Pending' },
    paused: { color: 'bg-orange-500/20 text-orange-400', label: 'Paused' },
    road_survey_done: { color: 'bg-blue-500/20 text-blue-400', label: 'Road Survey Done' },
    waiting_approval: { color: 'bg-purple-500/20 text-purple-400', label: 'Waiting Approval' },
    review_required: { color: 'bg-red-500/20 text-red-400', label: 'Review Required' },
    rejected: { color: 'bg-red-500/20 text-red-400', label: 'Rejected' },
    approved: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Approved' },
    sent_to_customer: { color: 'bg-indigo-500/20 text-indigo-400', label: 'Sent to Customer' },
    archived: { color: 'bg-gray-500/20 text-gray-400', label: 'Archived' }
  };

  // Listen for survey status changes
  useEffect(() => {
    const handleStatusChange = (event) => {
      const { surveyId, status } = event.detail;
      // Update the survey in the list
      const updatedSurveys = surveys.map(survey => 
        survey.id === surveyId ? { ...survey, status } : survey
      );
      // Force a re-render with updated data
      setSurveys(updatedSurveys);
    };

    window.addEventListener('surveyStatusChanged', handleStatusChange);
    return () => window.removeEventListener('surveyStatusChanged', handleStatusChange);
  }, [surveys]);

  // Handle survey load/unload
  useEffect(() => {
    // Listen for both storage events (other tabs) and custom events (current tab)
    const handleStorageChange = (e) => {
      if (e.key === 'activeSurveyId') {
        setLocalActiveSurveyId(e.newValue);
      } else if (e.key === 'isRecording') {
        setLocalIsRecording(e.newValue === 'true');
      }
    };

    const handleLocalStorageUpdate = (e) => {
      const { key, newValue } = e.detail;
      if (key === 'activeSurveyId') {
        setLocalActiveSurveyId(newValue);
      } else if (key === 'isRecording') {
        setLocalIsRecording(newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleLocalStorageUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleLocalStorageUpdate);
    };
  }, []);

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-400">
        Loading surveys...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="mt-8 bg-[#2A2B2E] rounded-lg p-6">
      <h2 className="text-lg font-semibold text-white mb-4">Recent Surveys</h2>
      <div className="space-y-4">
        {surveys.map(survey => (
          <div
            key={survey.id}
            className={`bg-[#2A2B2E] rounded-lg p-4 border-2 transition-colors relative ${
              survey.id === localActiveSurveyId
                ? localIsRecording
                  ? 'border-red-500 ring-2 ring-red-500/50' // Red with glow when recording
                  : 'border-green-500 ring-2 ring-green-500/50' // Green with glow when loaded
                : survey.isShared || survey.sharedWith?.includes(auth.currentUser?.uid)
                  ? 'border-blue-500/50' // Blue border for shared surveys
                  : 'border-gray-500/20' // Gray when not loaded
            }`}
          >
            {/* Header Section */}
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    {survey.name}
                    {survey.isShared && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                        Shared Survey
                      </span>
                    )}
                  </h3>
                  <span className="text-xs text-gray-400 bg-gray-700/50 px-2 py-0.5 rounded">
                    ID: {survey.id}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                  <span>Owner: {survey.ownerId === auth.currentUser?.uid || survey.ownerEmail === auth.currentUser?.email ? 'You' : survey.ownerEmail}</span>
                  <span>•</span>
                  <span className="font-medium">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs ${STATUS_CONFIG[survey.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                    {STATUS_CONFIG[survey.status]?.label || survey.status}
                  </span>
                  <span className="font-medium">Customer:</span>
                  <span className="text-white" title={survey.customerName}>
                    {survey.customerName}
                  </span>
                  <span>•</span>
                  <span>Created {formatDate(survey.createdAt)}</span>
                  {survey.sharedWith?.length > 0 && (
                    <>
                      <span>•</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSurvey(survey);
                          setShowMembers(true);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full hover:bg-blue-500/30"
                      >
                        <Users className="w-3 h-3" />
                        {survey.sharedWith.length} {survey.sharedWith.length === 1 ? 'member' : 'members'}
                      </button>
                    </>
                  )}
                  {survey.status === 'paused' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full text-xs">
                      <PauseCircle className="w-3 h-3" />
                      Tracking Paused
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    setSelectedSurvey(survey);
                    setShowEditSurvey(true);
                  }}
                  className="p-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded"
                  title="View Survey Information"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSurvey(survey);
                    setShowShare(true);
                  }}
                  className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded"
                  title="Share Settings"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSurvey(survey);
                    setShowDeleteModal(true);
                  }}
                  className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                  title="Delete Survey"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleSurveyLoad(survey.id)} 
                  disabled={false}
                  className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                    survey.id === localActiveSurveyId
                      ? isRecording
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  }`}
                >
                  {survey.id === localActiveSurveyId ? 'Unload' : 'Load'}
                </Button>
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
              {/* POIs and Routes */}
              <div className="bg-gray-700/50 p-2 rounded">
                <span className="text-gray-400">Points & Routes</span>
                <div className="text-white space-y-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span>{survey.stats?.poisCount || 0} POIs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-green-400" />
                    <span>{survey.stats?.routesCount || 0} Routes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-purple-400" />
                    <span>{survey.stats?.profileCount || 0} Profiles</span>
                  </div>
                </div>
              </div>

              {/* Distance and Files */}
              <div className="bg-gray-700/50 p-2 rounded">
                <span className="text-gray-400">Distance & Files</span>
                <div className="text-white space-y-1">
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-yellow-400" />
                    <span>{((survey.stats?.totalDistance || 0) / 1000).toFixed(1)} km</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-400" />
                    <span>{survey.stats?.fileCount || 0} Files</span>
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="bg-gray-700/50 p-2 rounded">
                <span className="text-gray-400">Vehicle</span>
                <div className="text-white space-y-1">
                  <p>{survey.vehicle?.type || 'N/A'}</p>
                  <p>{survey.vehicle ? 
                    `${survey.vehicle.length}' × ${survey.vehicle.width}' × ${survey.vehicle.height}'` : 
                    'No dimensions'}</p>
                </div>
              </div>

              {/* Route Info */}
              <div className="bg-gray-700/50 p-2 rounded">
                <span className="text-gray-400">Route Info</span>
                <div className="text-white space-y-1">
                  <p>Origin: {survey.origin || 'Not set'}</p>
                  <p>Destination: {survey.destination || 'Not set'}</p>
                  <p>High Pole: {survey.highPoleHeights?.required ? 
                    `${survey.highPoleHeights.value} ${survey.highPoleHeights.unit}` : 
                    'Not Required'}</p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {surveys.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No surveys found
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareSurveyModal
        isOpen={showShare}
        onClose={() => {
          setShowShare(false);
          setSelectedSurvey(null);
        }}
        survey={selectedSurvey}
      />
      
      {/* Shared Members Modal */}
      {showMembers && selectedSurvey && (
        <SharedMembersModal
          isOpen={showMembers}
          onClose={() => {
            setShowMembers(false);
            setSelectedSurvey(null);
          }}
          survey={selectedSurvey}
        />
      )}
      
      {/* Delete Survey Modal */}
      {showDeleteModal && selectedSurvey && (
        <DeleteSurveyModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedSurvey(null);
          }}
          survey={selectedSurvey}
        />
      )}
      
      {/* Edit Survey Modal */}
      {showEditSurvey && selectedSurvey && (
        <SurveyForm
          survey={selectedSurvey}
          isEdit={true}
          onClose={() => {
            setShowEditSurvey(false);
            setSelectedSurvey(null);
          }}
          onSave={async (data) => {
            await onEdit(data);
            setShowEditSurvey(false);
            setSelectedSurvey(null);
          }}
        />
      )}
    </div>
  );
};

export { SurveyList }